import { Card, CardContent, Grid } from '@mui/material';
import Loader from 'components/Loading/Loader';
import PageLayout from 'components/PageLayout';
import { EventWaitingLine } from 'lib/graphql/entities/waitingLines/types';
import { useGetAllEventWaitingLinesQuery, useGetOrganizationCurrencyQuery } from 'lib/graphql/graphql';
import { mergePaginatedList, usePagination } from 'lib/graphql/pagination';
import useEvent from 'providers/event/useEvent';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { SmallTitle, Title } from 'styles/styles';

import ReportTimeRangePicker from '../../../components/ReportTimeRangePicker/ReportTimeRangePicker';
import useEventDocumentTitle from '../useEventDocumentTitle';
import ObnlsBreakdownChart from './components/ObnlsBreakdownChart/ObnlsBreakdownChart';
import SalesBreakdownChart from './components/SalesBreakdownChart/SalesBreakdownChart';
import TotalTransactionsChart from './components/TotalTransactionsChart/TotalTransactionsChart';
import TransactionsGrid from './components/TransactionsGrid/TransactionsGrid';
import WaitingLineSelector from './components/WaitingLineSelector/WaitingLineSelector';
import WaitingLinesSalesChart from './components/WaitingLinesSalesChart/WaitingLinesSalesChart.tsx';
import { Content } from './styles';

function FinancialReport() {
  useEventDocumentTitle('financial_report');
  const {
    id: eventId,
    organizationId,
    startTime,
    timezone,
  } = useEvent();

  const [selectedTime, setSelectedTime] = useState('preset#all');
  const [selectedWaitingLine, setSelectedWaitingLine] = useState('all');

  const {
    data,
    hasNextPage,
    loading,
  } = usePagination(
    useGetAllEventWaitingLinesQuery({ variables: { eventId } }),
    (d) => d?.getAllEventWaitingLines.lastEvaluated,
    (existing, incoming) => ({
      getAllEventWaitingLines: mergePaginatedList(
        existing?.getAllEventWaitingLines,
        incoming?.getAllEventWaitingLines,
        'waitingLines',
      ),
    }),
  );
  const fetchingWaitingLines = loading || hasNextPage;
  const waitingLines = data?.getAllEventWaitingLines.waitingLines;

  const {
    data: currencyData,
    loading: currencyLoading,
  } = useGetOrganizationCurrencyQuery({ variables: { id: organizationId } });
  const currency = currencyData?.getOrganizationDetails?.currency || '';

  const waitingLinesMap = useMemo(() => (
    waitingLines?.reduce(
      (acc, waitingLine) => ({ ...acc, [waitingLine.id]: waitingLine }),
      {} as Record<string, EventWaitingLine>,
    ) || {}
  ), [waitingLines]);

  return (
    <PageLayout>
      <Content>
        <Title>
          <FormattedMessage id="financial_report" />
        </Title>
        <Loader isLoading={fetchingWaitingLines || currencyLoading}>
          <Grid container padding={2} gap={2}>
            <Grid item xs>
              <SalesBreakdownChart currency={currency} />
            </Grid>
            <Grid item xs>
              <TotalTransactionsChart waitingLinesMap={waitingLinesMap} />
            </Grid>
          </Grid>
          <Card>
            <CardContent>
              <Grid container gap={2}>
                <Grid item xs={12}>
                  <SmallTitle>
                    <FormattedMessage id="transactions" />
                  </SmallTitle>
                </Grid>
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item>
                      <ReportTimeRangePicker
                        selectedTime={selectedTime}
                        onChange={setSelectedTime}
                        startTime={startTime}
                        timezone={timezone}
                      />
                    </Grid>
                    <Grid item>
                      <WaitingLineSelector
                        selectedWaitingLine={selectedWaitingLine}
                        onChange={setSelectedWaitingLine}
                        waitingLines={waitingLines}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <Grid container padding={2} spacing={2}>
                    <Grid item xs>
                      <ObnlsBreakdownChart
                        selectedTime={selectedTime}
                        selectedWaitingLine={selectedWaitingLine}
                      />
                    </Grid>
                    <Grid item xs>
                      <WaitingLinesSalesChart
                        currency={currency}
                        selectedTime={selectedTime}
                        selectedWaitingLine={selectedWaitingLine}
                        waitingLinesMap={waitingLinesMap}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <TransactionsGrid
                    selectedTime={selectedTime}
                    selectedWaitingLine={selectedWaitingLine}
                    waitingLinesMap={waitingLinesMap}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Loader>
      </Content>
    </PageLayout>
  );
}

export default FinancialReport;
